import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Plus, Loader2, Users, X, Inbox, Search, LogOut } from "lucide-react";
import { argus, aErr, Btn, Label, inputCls, ARGUS_TOKEN } from "@/components/argus/ui";
import ArgusLogin from "@/components/argus/ArgusLogin";
import ArgusSearch from "@/components/argus/ArgusSearch";
import { ArgusLegalFooter } from "@/pages/ArgusLegal";
import ArgusPaywall from "@/components/argus/ArgusPaywall";
import ArgusCase from "@/components/argus/ArgusCase";
import ArgusTipInbox from "@/components/argus/ArgusTipInbox";

function Team({ me, onClose, setMe }) {
  const [email, setEmail] = useState(""); const [err, setErr] = useState("");
  const add = async () => {
    setErr("");
    try { const { data } = await argus.post("/members", { email }); setMe(m => ({ ...m, members: data.members })); setEmail(""); }
    catch (e) { setErr(aErr(e)); }
  };
  const remove = async (em) => { await argus.del(`/members/${encodeURIComponent(em)}`); setMe(m => ({ ...m, members: m.members.filter(x => x !== em) })); };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center px-4" data-testid="argus-team-modal">
      <div className="w-full max-w-md bg-[#06150e] border border-white/10 rounded-xl p-5">
        <div className="flex justify-between items-center"><Label>Team · {(me.members?.length || 0) + 1} / {me.seats} seats</Label><button type="button" onClick={onClose} data-testid="argus-team-close"><X className="w-4 h-4" /></button></div>
        <p className="text-xs text-white/50 mt-2">Members sign in to Fliboe with this email and see all shared case files.</p>
        <div className="flex gap-2 mt-3"><input className={inputCls} value={email} onChange={e => setEmail(e.target.value)} placeholder="reporter@newsroom.com" data-testid="argus-team-email" /><Btn onClick={add} data-testid="argus-team-add">Add</Btn></div>
        {err && <div className="text-red-300 text-xs mt-2" data-testid="argus-team-err">{err}</div>}
        <ul className="mt-3 space-y-1">{(me.members || []).map(m => <li key={m} className="flex justify-between text-sm bg-white/[0.03] rounded px-2 py-1">{m}<button type="button" className="text-red-300 text-xs" onClick={() => remove(m)} data-testid={`argus-team-remove-${m}`}>Remove</button></li>)}</ul>
      </div>
    </div>
  );
}

export default function Argus() {
  const [user, setUser] = useState(undefined); const [login, setLogin] = useState(false);
  const [me, setMe] = useState(null); const [cases, setCases] = useState([]); const [active, setActive] = useState(null);
  const [newTitle, setNewTitle] = useState(""); const [busy, setBusy] = useState(false); const [team, setTeam] = useState(false); const [err, setErr] = useState("");
  const [view, setView] = useState("cases");
  const [showPlans, setShowPlans] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem(ARGUS_TOKEN)) { setUser(null); return; }
    argus.get("/auth/me").then(r => setUser(r.data)).catch(() => { localStorage.removeItem(ARGUS_TOKEN); setUser(null); });
  }, []);
  const logout = () => { localStorage.removeItem(ARGUS_TOKEN); setUser(null); setMe(null); setCases([]); setActive(null); setView("cases"); setShowPlans(false); };
  const openCase = (id) => {
    argus.get("/cases").then(c => { setCases(c.data.cases || []); setActive(id); setView("cases"); });
  };

  useEffect(() => {
    document.title = "Argus · Investigative research desk";
    if (user === undefined) return;
    if (!user) { setMe({ active: false }); return; }
    argus.get("/me").then(r => {
      setMe(r.data);
      if (r.data.active) argus.get("/cases").then(c => { setCases(c.data.cases || []); setActive(c.data.cases?.[0]?.case_id || null); });
    }).catch(() => setMe({ active: false }));
  }, [user]);

  useEffect(() => {
    const refresh = () => argus.get("/me").then(r => setMe(m => ({ ...m, ...r.data }))).catch(() => {});
    window.addEventListener("argus-used", refresh);
    return () => window.removeEventListener("argus-used", refresh);
  }, []);


  const create = async () => {
    setBusy(true); setErr("");
    try { const { data } = await argus.post("/cases", { title: newTitle.trim() }); setCases(c => [data, ...c]); setActive(data.case_id); setNewTitle(""); }
    catch (e) { setErr(aErr(e)); } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen argus-root argus-bg text-[#f0fdf4]" data-testid="argus-page">
      <header className="border-b border-white/10 px-3 sm:px-5 py-3 flex items-center gap-2 sm:gap-3">
        <Link to="/argus" className="flex items-center gap-2"><Eye className="w-5 h-5 text-[#10b981]" /><span className="argus-display text-xl font-bold tracking-tight">Argus</span></Link>
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40 hidden sm:inline">Private research desk</span>
        <div className="flex-1" />
        {me?.active && (
          <>
            <span className="text-[11px] font-mono text-white/60 whitespace-nowrap" data-testid="argus-usage"><span className="hidden sm:inline">{me.label} · </span>{me.used}{me.cap ? `/${me.cap}` : ""} <span className="hidden sm:inline">AI actions this month</span><span className="sm:hidden">AI</span></span>
            {me.members && <Btn kind="ghost" onClick={() => setTeam(true)} data-testid="argus-team-btn"><Users className="w-3.5 h-3.5" /> Team</Btn>}
          </>
        )}
        {me?.active && <Btn kind="ghost" onClick={() => setShowPlans(p => !p)} data-testid="argus-plans-btn">{showPlans ? "Back to workspace" : "Plans"}</Btn>}
        {user ? (
          <>
            <span className="text-[11px] text-white/50 hidden md:inline truncate max-w-[180px]" data-testid="argus-user-email">{user.email}</span>
            <Btn kind="ghost" onClick={logout} className="whitespace-nowrap" data-testid="argus-logout-btn"><LogOut className="w-3.5 h-3.5" /><span className="hidden sm:inline">Log out</span></Btn>
          </>
        ) : user === null && <Btn onClick={() => setLogin(true)} data-testid="argus-signin-btn">Sign in</Btn>}
      </header>

      {!me ? <div className="p-10 text-white/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : (!me.active || showPlans) ? <ArgusPaywall me={me} loggedIn={!!user} onSignIn={() => setLogin(true)} />
        : (
          <div className="grid md:grid-cols-[280px_1fr] min-h-[calc(100vh-53px)]">
            <aside className="border-r border-white/10 p-4 space-y-3" data-testid="argus-sidebar">
              <button type="button" onClick={() => setView(v => v === "search" ? "cases" : "search")} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${view === "search" ? "bg-[#10b981] text-black border-[#10b981]" : "border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10"}`} data-testid="argus-search-nav">
                <Search className="w-4 h-4" /> Records search
              </button>
              {me.plan && (
                <button type="button" onClick={() => setView(v => v === "inbox" ? "cases" : "inbox")} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${view === "inbox" ? "bg-[#10b981] text-black border-[#10b981]" : "border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10"}`} data-testid="argus-inbox-btn">
                  <Inbox className="w-4 h-4" /> Whistleblower tip inbox
                </button>
              )}
              <div className="flex gap-2">
                <input className={inputCls} value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => e.key === "Enter" && newTitle.trim().length > 1 && create()} placeholder="New case title" data-testid="argus-new-case-title" />
                <Btn busy={busy} onClick={create} disabled={newTitle.trim().length < 2} data-testid="argus-new-case"><Plus className="w-3.5 h-3.5" /></Btn>
              </div>
              {err && <div className="text-red-300 text-xs">{err}</div>}
              <Label>Case files</Label>
              <div className="space-y-1 max-h-[40vh] md:max-h-none overflow-y-auto">
                {cases.length === 0 && <div className="text-xs text-white/40">No cases yet. Start one above.</div>}
                {cases.map(c => (
                  <button key={c.case_id} type="button" onClick={() => { setActive(c.case_id); setView("cases"); }} className={`w-full text-left px-3 py-2 rounded-md transition-colors ${view === "cases" && active === c.case_id ? "bg-[#10b981]/15 border border-[#10b981]/40" : "hover:bg-white/5 border border-transparent"}`} data-testid={`argus-case-item-${c.case_id}`}>
                    <div className="text-sm font-semibold truncate">{c.title}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{c.status}</div>
                  </button>
                ))}
              </div>
            </aside>
            <section className="min-w-0">
              {view === "search" ? <ArgusSearch cases={cases} /> : view === "inbox" ? <ArgusTipInbox me={me} onOpenCase={openCase} /> : active ? <ArgusCase key={active} caseId={active}
                onUpdated={(d) => setCases(cs => cs.map(x => x.case_id === d.case_id ? { ...x, ...d } : x))}
                onDeleted={(id) => { const rest = cases.filter(x => x.case_id !== id); setCases(rest); setActive(rest[0]?.case_id || null); }} />
                : <div className="p-10 text-white/45 argus-display text-xl">Open a case file, or start a new investigation.</div>}
            </section>
          </div>
        )}
      {login && <ArgusLogin onClose={() => setLogin(false)} onDone={(u) => { setLogin(false); setMe(null); setUser(u); }} />}
      <ArgusLegalFooter />
      {team && me?.members && <Team me={me} setMe={setMe} onClose={() => setTeam(false)} />}
    </main>
  );
}
